/**
Project: Voice Panel (c)
Title: Numbers List
Description: Component to display the numbers list and also to Add, Buy, Export and Import the numbers.
Copyrights: This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this source code package.
*/
import {
    Button,
    Checkbox,
    CloseIcon,
    Dialog,
    Divider,
    Flex,
    RetryIcon,
    Text,
  } from "@fluentui/react-northstar";
  import axios from "axios";
  import moment from "moment";
  import React, { Component } from "react";
  import { Col, Row } from "react-flexbox-grid";
  import { connect } from "react-redux";
  import { AppConfigProps } from "../../../../core/settings/app-config";
  import AlertErrorMessage from "../../common/helpers/alert-error-message";
  import PageLoader from "../../common/helpers/page-loader";
  import { VoiceAdminConstants } from "../../common/settings/voice-admin-constants";
  import { VoiceAdminURLProps } from "../../common/settings/voice-admin-urls";
  
  import TokenUtil from "../../../../core/helpers/token-util";
  import PageUtil from "../../../../core/helpers/page-util";
  import IntlUtil from "../../../../core/helpers/intl-util";
  import ResponseUtil from "../../../../core/helpers/response-util";
  import { manageError } from "../../../../core/actions/common-actions";
  import {
    getTenantAccountsNumberSyncNow,
    tenentAccountNumberSyncNow,
  } from "../actions/general-actions";
  
  class NumbersSyncDialog extends Component {
    _isMounted = false;
    _axiosSource = axios.CancelToken.source();
    _cancelToken = { cancelToken: this._axiosSource.token };
    _intl_ns_common = "common";
    _intl_ns_vpa_common = "vpa_common";
    constructor(props) {
      super(props);
      this.state = {
        isPageDataFetched: false,
        isSyncTenantAccountsFetched: false,
        isSyncProcessStarted: false,
        showSyncDialog: false,
        isDisableAgreeSync: true,
        syncTenantAccountRecords: null,
      };
    }
    setStateAsync = (state) => {
      if (this._isMounted) {
        return new Promise((resolve) => {
          this.setState(state, resolve);
        });
      }
    };
    async componentDidMount() {
      this._isMounted = true;
      PageUtil.scrollToTop();
      await this.loadPageData();
    }
  
    componentWillUnmount() {
      this._isMounted = false;
      this._axiosSource.cancel(
        IntlUtil.getText(
          this._intl_ns_common,
          "notification.warning.requestCancelled"
        )
      );
    }
  
    loadPageData = async () => {
      if (this.props.isNumberInventoryPage) {
        await this.fetchNumberSyncData();
      } else {
        if (
          this.props.syncResponse?.processStatus.toLowerCase() !==
          VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED.toLowerCase()
        ) {
          await this.setStateAsync({
            isSyncDataPropagated: false,
            isSyncProcessStarted: true,
          });
        } else {
          await this.setStateAsync({
            isSyncDataPropagated: true,
            isSyncProcessStarted: false,
          });
        }
        await this.setStateAsync({
          syncTenantAccountRecords: this.props.syncResponse,
          isSyncTenantAccountsFetched: true,
        });
      }
      // await this.setStateAsync({ isSyncTenantAccountsFetched: false });
    };
  
    getNumbersSyncInfoText = (timeStamp) => {
      if (timeStamp) {
        let propagationTimestamp = timeStamp.createTimestamp;
        if (
          timeStamp.endTimestamp &&
          timeStamp.endTimestamp !== "0001-01-01T00:00:00"
        ) {
          propagationTimestamp = timeStamp.endTimestamp;
        }
        const timestampObj = moment(propagationTimestamp);
        const dateString = timestampObj.format("ll");
        const timeString = timestampObj.format("LTS");
  
        let text = IntlUtil.getText(
          this._intl_ns_vpa_common,
          "notification.info.lastSyncDate"
        );
        if (timestampObj && dateString && timeString) {
          text =
            text +
            " " +
            IntlUtil.getSubstituteText(
              this._intl_ns_vpa_common,
              "notification.info.numbersDataSyncDateTime",
              [
                {
                  key: "<DATE>",
                  value: dateString,
                },
                {
                  key: "<TIME>",
                  value: timeString,
                },
              ]
            );
        }
        return text;
      } else return "";
    };
  
    refreshNumberSyncData = async () => {
      await this.fetchNumberSyncData();
    };
  
    fetchNumberSyncData = async () => {
      await this.setStateAsync({ isSyncTenantAccountsFetched: false });
      await getTenantAccountsNumberSyncNow(this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data.result
          ) {
            const syncNumResponse = res.data.result;
            await this.setStateAsync({
              syncTenantAccountRecords: syncNumResponse,
              isDisableAgreeSync: true,
            });
            await this.props.syncResponseUpdate(syncNumResponse);
            if (
              syncNumResponse.processStatus.toLowerCase() !==
              VoiceAdminConstants.USN_DATA_PROPAGATION.OVERALL_STATUS_COMPLETED.toLowerCase()
            ) {
              await this.setStateAsync({ isSyncDataPropagated: false });
              if (this.props.syncNumbersInProgressUpdate)
                await this.props.syncNumbersInProgressUpdate(true);
            } else {
              await this.setStateAsync({ isSyncDataPropagated: true });
              if (this.props.syncNumbersInProgressUpdate)
                await this.props.syncNumbersInProgressUpdate(false);
              if (this.state.isSyncProcessStarted === true) {
                await this.props.numbersSyncCompleted();
                await this.setStateAsync({ isSyncProcessStarted: false });
              }
            }
          } else {
            await this.setStateAsync({ isSyncDataPropagated: true });
            if (this.props.syncNumbersInProgressUpdate)
              await this.props.syncNumbersInProgressUpdate(true);
          }
          await this.setStateAsync({ isSyncTenantAccountsFetched: true });
        })
        .catch(async (err) => {
          await this.setStateAsync({ isSyncTenantAccountsFetched: true });
          await manageError(err, this.props.history);
        });
    };
  
    handleSyncNumber = async () => {
      if (this.props.numberRemoved) await this.props.resetNumberRemoval();
      await this.setStateAsync({
        isSyncTenantAccountsFetched: false,
        isSyncProcessStarted: true,
        errorMessage: null,
        errorDetails: null,
        successMessage: null,
      });
  
      const data = {
        refreshToken: TokenUtil.getIdentitySippioRefreshToken(),
      };
      await tenentAccountNumberSyncNow(data, this._cancelToken)
        .then(async (res) => {
          if (
            res &&
            res.status === AppConfigProps.httpStatusCode.ok &&
            res.data.result
          ) {
            await this.fetchNumberSyncData();
          }
          await this.setStateAsync({ isSyncTenantAccountsFetched: true });
        })
        .catch(async (err) => {
          await this.setStateAsync({ isSyncTenantAccountsFetched: true });
          const errorStatus = ResponseUtil.getStatus(err);
          if (
            errorStatus &&
            errorStatus === AppConfigProps.httpStatusCode.badRequest
          ) {
            const primeError = ResponseUtil.getPrimeError(err);
            if (
              primeError &&
              primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE.VOICE_SETUP_NOTCOMPLETED
            ) {
              this.props.history.push(VoiceAdminURLProps.setup);
              return;
            }
  
            if (
              primeError &&
              primeError.code ===
                VoiceAdminConstants.SERVER_ERROR_CODE
                  .NUMBER_SYNC_ALREADY_INPROGRESS
            ) {
              await this.setStateAsync({
                errorDetails: primeError.message,
              });
            }
  
            await this.setStateAsync({
              errorMessage: IntlUtil.getText(
                this._intl_ns_vpa_common,
                "notification.error.numbersSyncAlreadyProgress"
              ),
              showSyncAlert: true,
            });
          } else {
            await manageError(err, this.props.history);
          }
        });
    };
  
    enableNumberSync = async (e, option) => {
      await this.setStateAsync({ isDisableAgreeSync: !option.checked });
    };
  
    getNumbersPropagationInfoText = (propagation) => {
      if (propagation) {
        let propagationTimestamp = propagation.createTimestamp;
        if (
          propagation.endTimestamp &&
          propagation.endTimestamp !== "0001-01-01T00:00:00"
        ) {
          propagationTimestamp = propagation.endTimestamp;
        }
        const timestampObj = moment(propagationTimestamp);
        const dateString = timestampObj.format("ll");
        const timeString = timestampObj.format("LTS");
  
        let text = IntlUtil.getText(
          this._intl_ns_vpa_common,
          "notification.info.numbersDataPropagationCompleted"
        );
        if (timestampObj && dateString && timeString) {
          text =
            text +
            " " +
            IntlUtil.getSubstituteText(
              this._intl_ns_vpa_common,
              "notification.info.numbersDataPropagationDateTime",
              [
                {
                  key: "<DATE>",
                  value: dateString,
                },
                {
                  key: "<TIME>",
                  value: timeString,
                },
              ]
            );
        }
        return text;
      } else return "";
    };
  
    render() {
      return (
        <Dialog
          className="vpa-sync-status-dialog"
          content={
            <>
              <Flex space="between" gap="gap.large" vAlign="center">
                <Text className="page-text text-semibold vpa-dialog-header text-fc-primary">
                  {IntlUtil.getText(
                    this._intl_ns_vpa_common,
                    "content.numbersSync"
                  )}
                </Text>
                <Flex gap="gap.small">
                  {this.state.isSyncDataPropagated === false ? (
                    <Button
                      onClick={() => this.refreshNumberSyncData()}
                      icon={<RetryIcon />}
                      iconOnly
                      title="Refresh"
                    />
                  ) : null}
                  <Button
                    onClick={() => this.props.closeNumberSyncDialog()}
                    icon={<CloseIcon />}
                    iconOnly
                    title="Close"
                  />
                </Flex>
              </Flex>
              <Divider size={1} />
              {this.state.showSyncAlert === true ? (
                <Row>
                  <Col xs={12} lg={9} className="m-t-5">
                    {this.state.errorMessage ? (
                      <AlertErrorMessage
                        content={this.state.errorMessage}
                        moreDetails={{
                          label: IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "content.moreDetails"
                          ),
                          content: this.state.errorDetails,
                        }}
                      />
                    ) : null}
                  </Col>
                </Row>
              ) : null}
              <PageLoader
                status={this.state.isSyncTenantAccountsFetched}
                size="medium"
                labelPosition="below"
                label={IntlUtil.getText(
                  this._intl_ns_common,
                  "content.loadingInprogress"
                )}
                type="center"
              >
                <div>
                  {this.state.isSyncDataPropagated === false ? (
                    <>
                      <div className="m-t-30 m-b-110">
                        <Text className="page-text">
                          {IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "notification.info.syncProgress"
                          )}
                        </Text>
                      </div>
                      <div></div>
                    </>
                  ) : this.props.numberRemoved ? (
                    <>
                      <div className="m-t-30 m-b-40">
                        <Text className="page-text">
                          {this.props.numberRemovalMessage}
                        </Text>
                      </div>
                      <div className="m-t-10 text-fc-dark-grey">
                        <Text className="page-text">
                          {IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "notification.info.syncAccountUsersInfo"
                          )}
                        </Text>
                      </div>
                      <div className="m-t-5">
                        <Checkbox
                          disabled={
                            !this.state.isSyncDataPropagated ||
                            this.props.dataUpdateInProgress
                          }
                          onChange={(e, option) =>
                            this.enableNumberSync(e, option)
                          }
                          label={IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "notification.info.agreeSyncUsersInfo"
                          )}
                        />
                      </div>
                    </>
                  ) : this.props.dataUpdateInProgress ? (
                    <>
                      <div className="m-t-30 m-b-10">
                        <Text className="page-text">
                          {this.props.dataUpdateMessage}
                        </Text>
                      </div>
                      <div className="m-b-80">
                        <Text className="page-text">
                          {this.props.dataUpdateMessageTwo}
                        </Text>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="m-t-10 m-b-20">
                        <Text className="page-text">
                          <span>
                            {this.props.syncNumbersMessage ??
                              IntlUtil.getText(
                                this._intl_ns_vpa_common,
                                "notification.info.syncNumbersMessage"
                              )}
                          </span>
                        </Text>
                      </div>
                      <div className="m-t-10 m-b-20 text-fc-primary text-semibold">
                        <Text className="page-text">
                          {this.getNumbersSyncInfoText(
                            this.state.syncTenantAccountRecords
                          )}
                        </Text>
                        {this.state.syncTenantAccountRecords &&
                        this.state.syncTenantAccountRecords.numbersUpdateCount !==
                          null ? (
                          <Text
                            dangerouslySetInnerHTML={{
                              __html: IntlUtil.getSubstituteText(
                                this._intl_ns_vpa_common,
                                "notification.info.numberUpdateCount",
                                [
                                  {
                                    key: "<NUMBERSUPDATECOUNT>",
                                    value: this.state.syncTenantAccountRecords
                                      .numbersUpdateCount
                                      ? this.state.syncTenantAccountRecords
                                          .numbersUpdateCount
                                      : "0",
                                  },
                                ]
                              ),
                            }}
                            className="page-text m-l-10"
                          />
                        ) : null}
                      </div>
                      <div className="m-t-10 text-fc-dark-grey">
                        <Text className="page-text">
                          {IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "notification.info.syncAccountUsersInfo"
                          )}
                        </Text>
                      </div>
                      <div className="m-t-5">
                        <Checkbox
                          disabled={
                            !this.state.isSyncDataPropagated ||
                            this.props.dataUpdateInProgress
                          }
                          onChange={(e, option) =>
                            this.enableNumberSync(e, option)
                          }
                          label={IntlUtil.getText(
                            this._intl_ns_vpa_common,
                            "notification.info.agreeSyncUsersInfo"
                          )}
                        />
                      </div>
                    </>
                  )}
  
                  <div className="m-t-20">
                    <Button
                      disabled={
                        !this.state.isSyncDataPropagated ||
                        this.state.isDisableAgreeSync ||
                        this.props.dataUpdateInProgress
                      }
                      primary
                      content={IntlUtil.getText(
                        this._intl_ns_vpa_common,
                        "content.sync"
                      )}
                      onClick={() => this.handleSyncNumber()}
                      className="vpa-usn-button m-r-20"
                    />
                  </div>
                </div>
              </PageLoader>
            </>
          }
          open={this.props.showSyncDialog}
        />
      );
    }
  }
  const mapStateToProps = (state) => ({});
  
  const mapActionToProps = {};
  
  export default connect(mapStateToProps, mapActionToProps)(NumbersSyncDialog);
  